import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BlockchainValue } from '@/pages/FinancesPage/components/BlockchainSelector';

/**
 * Finance context type
 * - personal: User's personal wallet (no merchant)
 * - merchant: Selected merchant's wallet
 */
export type FinanceContextType = 'personal' | 'merchant';

/**
 * Finance Store State
 */
interface FinanceState {
  /** Current context type (personal or merchant) */
  contextType: FinanceContextType;

  /** Selected merchant GUID (null when in personal mode) */
  selectedMerchant: string | null;

  /** Selected blockchain for filtering ('all' | 'tron' | 'solana' | 'bsc') */
  selectedBlockchain: BlockchainValue;

  /** Set finance context */
  setContext: (type: FinanceContextType, merchantGuid?: string | null) => void;

  /** Set selected blockchain */
  setSelectedBlockchain: (blockchain: BlockchainValue) => void;

  /** Reset to personal wallet mode */
  resetContext: () => void;
}

/**
 * Finance Store
 *
 * Manages the finance page context (Personal Wallet vs Merchant).
 * Persists state to localStorage for continuity across sessions.
 *
 * @example
 * ```tsx
 * const { contextType, selectedMerchant, setContext } = useFinanceStore();
 *
 * // Switch to personal mode
 * setContext('personal');
 *
 * // Switch to merchant mode
 * setContext('merchant', 'merchant-guid-123');
 * ```
 */
export const useFinanceStore = create<FinanceState>()(
  persist(
    (set) => ({
      // Initial state
      contextType: 'personal',
      selectedMerchant: null,
      selectedBlockchain: 'all',

      // Actions
      setContext: (type: FinanceContextType, merchantGuid: string | null = null) => {
        set({
          contextType: type,
          selectedMerchant: type === 'merchant' ? merchantGuid : null,
        });
      },

      setSelectedBlockchain: (blockchain) => {
        set({ selectedBlockchain: blockchain });
      },

      resetContext: () => {
        set({
          contextType: 'personal',
          selectedMerchant: null,
          selectedBlockchain: 'all',
        });
      },
    }),
    {
      name: 'finance_context', // localStorage key
      version: 2, // Increment version for new field
    }
  )
);
